/**
 * SkeletonLoader — Reusable loading skeleton components
 */

/** Single skeleton line */
export function SkeletonLine({ width = '100%', height = 16 }: { width?: string | number; height?: number }) {
  return (
    <div
      className="animate-pulse rounded bg-muted"
      style={{ width, height }}
    />
  );
}

/** Card-shaped skeleton */
export function SkeletonCard() {
  return (
    <div className="rounded-xl border border-border bg-card p-4 animate-pulse">
      <div className="space-y-3">
        <SkeletonLine width="60%" height={20} />
        <SkeletonLine width="40%" height={14} />
        <SkeletonLine width="100%" height={14} />
        <SkeletonLine width="80%" height={14} />
      </div>
    </div>
  );
}

/** Novel card skeleton with cover image area (for Plaza grids) */
export function SkeletonNovelCard() {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card animate-pulse">
      <div className="aspect-[3/4] bg-muted" />
      <div className="flex flex-col gap-2 p-4">
        <SkeletonLine width="66%" height={20} />
        <SkeletonLine width="33%" height={12} />
        <SkeletonLine width="100%" height={16} />
        <SkeletonLine width="80%" height={16} />
      </div>
    </div>
  );
}

/** Grid of skeleton cards */
export function SkeletonGrid({ count = 6, cols = 3 }: { count?: number; cols?: number }) {
  const colClass =
    cols === 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
      : cols === 3
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : 'grid-cols-1 sm:grid-cols-2';
  return (
    <div className={`grid gap-4 ${colClass}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={`skel-${i}`} />
      ))}
    </div>
  );
}

/** List of skeleton rows (for Workshop/Chapter lists) */
export function SkeletonList({ count = 3, height = 56 }: { count?: number; height?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={`skel-${i}`} className="rounded-lg border border-border bg-card animate-pulse" style={{ height }} />
      ))}
    </div>
  );
}

/** Full-page content skeleton (for ReaderPage, NovelDetailPage etc.) */
export function SkeletonPage({ lines = 8, maxWidth = '3xl' }: { lines?: number; maxWidth?: string }) {
  const widths = [33, 66, 95, 88, 92, 78, 100, 85, 90, 73, 97, 82];
  return (
    <div className={`mx-auto max-w-${maxWidth} animate-pulse p-8`}>
      <SkeletonLine width="33%" height={24} />
      <div className="mt-4">
        <SkeletonLine width="66%" height={16} />
      </div>
      <div className="mt-8 space-y-3">
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonLine key={`skel-${i}`} width={`${widths[i % widths.length]}%`} height={16} />
        ))}
      </div>
    </div>
  );
}

/** Full-page centered spinner */
export function PageSpinner({ message }: { message?: string }) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center gap-3">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      {message && <p className="text-muted-foreground">{message}</p>}
    </div>
  );
}

/** Small inline spinner (for panels/sidebars) */
export function InlineSpinner({ size = 6, message }: { size?: number; message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-8">
      <div
        className="animate-spin border-2 border-primary border-t-transparent rounded-full"
        style={{ width: size * 4, height: size * 4 }}
      />
      {message && <p className="text-xs text-muted-foreground">{message}</p>}
    </div>
  );
}